import type { ToolCall } from '../../../../../utils/interface-types';
import McpWidgetContainer from '../mcp-widget-container';
import {
  extractToolCallImages,
  getToolCallErrorText,
  toolCallHasVisualOutput,
  toolCallHasWidget,
} from '../../lib/mcp-tool-utils';

type ToolCallCardProps = {
  toolCall: ToolCall;
};

function McpImages({ images }: { images: ReturnType<typeof extractToolCallImages> }) {
  if (images.length === 0) {
    return null;
  }

  return (
    <div className="p-2">
      <div className="grid grid-cols-2 gap-2">
        {images.map((image) => {
          const CardWrapper = image.href ? 'a' : 'div';
          const cardProps = image.href
            ? {
                href: image.href,
                target: '_blank' as const,
                rel: 'noopener noreferrer',
              }
            : {};

          return (
            <CardWrapper
              key={image.id}
              {...cardProps}
              className="block rounded-lg overflow-hidden border border-gray-100"
            >
              <img
                src={image.url}
                alt={image.alt || 'MCP result'}
                className="w-full h-auto block"
                referrerPolicy="no-referrer"
                loading="lazy"
                decoding="async"
              />
            </CardWrapper>
          );
        })}
      </div>
    </div>
  );
}

function ToolCallCard({ toolCall }: ToolCallCardProps) {
  const hasError = Boolean(toolCall.mcp_error);
  const errorText = getToolCallErrorText(toolCall.mcp_error);
  const hasWidget = toolCallHasWidget(toolCall);
  const images = hasWidget ? [] : extractToolCallImages(toolCall);
  if (!toolCallHasVisualOutput(toolCall)) {
    return null;
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
      {hasError && (
        <div className="flex items-start gap-2 p-3">
          <span className="text-red-500 text-base leading-none mt-0.5">⚠</span>
          <p className="text-xs text-red-600 leading-relaxed">{errorText}</p>
        </div>
      )}

      {!hasError && hasWidget && toolCall.mcp_id && toolCall.ui_resource_uri && (
        <div className="p-2">
          <McpWidgetContainer
            mcpId={toolCall.mcp_id}
            resourceUri={toolCall.ui_resource_uri}
            toolName={toolCall.tool_name}
            toolInput={toolCall.widget_arguments}
            toolResult={toolCall.widget_result}
          />
        </div>
      )}

      {!hasError && !hasWidget && <McpImages images={images} />}
    </div>
  );
}

export default ToolCallCard;
